import { captureException } from '@sentry/react'
import { z } from 'zod'

/**
 * What one publication on the directory channel says, as it comes off the
 * wire.
 *
 * Assembled in exactly two places, both in
 * `data-plane/crates/dataplane/src/realtime/events.rs`. Twelve event types
 * name one node — `nodeId`, `parentId`, `actorId`, and a test there asserts
 * that shape and no fifth field. One, `trash.emptied` (OPSSTD-8437), does not:
 * an emptying can accept thousands, so it names `parentIds` and a `count`
 * instead of every id, and has its own test for that shape. This is the
 * reading half of both contracts, in one object because `type` is read as a
 * bare string rather than as a discriminant — see below — so a union keyed on
 * it would not narrow anything a reader could use.
 *
 * **It is not in the generated SDK and cannot be.** The payload travels over
 * Centrifugo, not over HTTP, so it is in no OpenAPI document and `@repo/sdk`
 * has nothing to offer here. That is the one case where parsing a shape by
 * hand is right rather than the duplicate validation `sdk.md` forbids: there
 * is no generated type to duplicate.
 *
 * **`type` is a bare string on purpose.** The set the server publishes is
 * closed, but closing it *here* would mean a new event type failing to parse
 * and vanishing silently — the transport dropping something it was only ever
 * meant to carry. The set is closed one layer up, where events are turned
 * into invalidations (T11.3.2), which is also where a test can hold the list
 * in step with `events.rs`. An unmapped type there is a visible gap; an
 * unparsed one here would not be.
 *
 * **`parentId` and `actorId` are nullable and never absent, on a singular
 * event.** The publisher sends JSON `null` rather than omitting the key,
 * precisely so a reader can tell *at the top of the tree* from *the server
 * did not say*. `.nullable()` without `.optional()` is what holds that for
 * `actorId`, which every event carries; `nodeId` and `parentId` are
 * `.optional()` as well, because `trash.emptied` carries neither — there is
 * no single node or parent to name null rather than absent for.
 *
 * **`parentIds` and `count` are `.optional()`, present on `trash.emptied`
 * alone.** A twelfth key on every other event's payload, absent in practice
 * but present as `null` for symmetry, would be exactly the widening the
 * payload's own restraint (a fifth field is a disclosure, not a convenience)
 * argues against on the other four fields — so instead the two shapes share
 * one object and the fields each does not use are the ones it never sends.
 */
const zDirectoryEvent = z.object({
	type: z.string(),
	/** Absent on `trash.emptied`, which names no single node. */
	nodeId: z.string().optional(),
	/** Absent on `trash.emptied` — see [`parentIds`]. */
	parentId: z.string().nullable().optional(),
	/**
	 * The distinct folders a `trash.emptied` accepted its tops out of, `null`
	 * among them once when at least one had none — a personal root, or a
	 * folder a move already carried out from under one. Bounded by the number
	 * of distinct origins rather than by how many nodes were emptied, which is
	 * the whole reason this event exists rather than one `node.purged` per
	 * item. Absent on every other event.
	 */
	parentIds: z.array(z.string().nullable()).optional(),
	/**
	 * How many of the tops a `trash.emptied` accepted **this recipient**
	 * reaches — not the emptying's total, which nobody but the server ever
	 * computes. Absent on every other event.
	 */
	count: z.number().optional(),
	/**
	 * Who caused it, so a client can drop its own echo — `null` when nobody
	 * did, which is the reconciliation sweep publishing for an uploader who is
	 * long gone. A client that read `null` as "me" would suppress the one
	 * event it most needs. Present on every event, `trash.emptied` included:
	 * the caller's own other tabs still have to redraw.
	 */
	actorId: z.string().nullable()
})

export type DirectoryEvent = z.infer<typeof zDirectoryEvent>

/**
 * Everything the feed tells its consumers, as one union.
 *
 * One channel of delivery rather than two callbacks, because the two carry an
 * ORDER between them that separate callbacks would lose. A `desync` says
 * "everything you hold is suspect"; an `event` says "this one node changed".
 * Interleaved wrongly they contradict each other, and the consumer has no way
 * to re-establish which came first.
 */
export type DirectorySignal =
	| { kind: 'event'; event: DirectoryEvent }
	/**
	 * The subscription came back and Centrifugo could NOT replay the gap.
	 *
	 * This is not an error and must not be hidden as one: the `directory`
	 * namespace is configured `force_recovery: true` with `history_size: 200`
	 * and `history_ttl: 600s`, so recovery fails exactly when the client was
	 * away longer than ten minutes or missed more than two hundred events.
	 * Both are ordinary — a laptop lid closed over lunch is one. What is NOT
	 * ordinary is continuing as though nothing happened, because the events
	 * that would have corrected the view are gone. The only correct response
	 * is to re-read from the server.
	 */
	| { kind: 'desync' }

/**
 * Turn a publication payload into a signal, or answer `undefined`.
 *
 * The argument is `unknown` because Centrifugo's `data` is `any` and the
 * server on the other end is a different process, a different language and a
 * different release cadence. A payload that does not parse is dropped and
 * reported rather than thrown: this runs inside an event emitter with no
 * caller to catch it, and one malformed publication must not take the
 * subscription down with it.
 *
 * Reported, though, and not swallowed. A payload this cannot read means the
 * two sides have drifted, and the visible symptom would otherwise be a file
 * manager that stops noticing changes — the class of silence that takes months
 * to attribute. Sentry is dormant without a DSN, so this costs nothing in
 * development and says so in production.
 */
export function toDirectorySignal(data: unknown): DirectorySignal | undefined {
	const parsed = zDirectoryEvent.safeParse(data)

	if (!parsed.success) {
		captureException(parsed.error, {
			tags: { feature: 'directory-feed' },
			extra: { payload: data }
		})
		return undefined
	}

	return { kind: 'event', event: parsed.data }
}
